import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileText, Folder, Info, Loader2, Plus, Power, PowerOff, RefreshCw, Search, ShieldCheck, Sparkles } from 'lucide-react';
import {
  adminCustomTaxonomy,
  adminEisf,
  type DocumentTypeWithApprovers,
  type CatalogScope,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useHasRole } from '@/components/RoleGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { ConfirmModal } from '@/components/layout/ConfirmModal';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Path C — eISF Master Data console rendered as a 4-level collapsible
 * tree (Zone → Section → Artifact → Sub-artifact).
 *
 * The taxonomy (zones/sections/artifacts/sub-artifacts) is sourced from
 * `/admin/eisf/taxonomy`. The catalog rows (per-document-type rules:
 * approvers, workflows, naming, etc.) come from
 * `/admin/eisf/document-types`. The tree node we treat as the clickable
 * unit is the SUB-ARTIFACT — that's where the document type lives in the
 * binder taxonomy. We match a sub-artifact to a catalog row by exact
 * `document_type_name`. Nodes without a matching row render a muted
 * "+ Define rules" CTA that pre-fills the create dialog.
 */

type TaxonomyResponse = Awaited<ReturnType<typeof adminEisf.getTaxonomy>>;
type TaxonomyZone = TaxonomyResponse['zones'][number];
type TaxonomySection = TaxonomyZone['sections'][number];

export function EisfMasterDataPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatform = user?.role === 'platform_admin' || user?.role === 'application_admin';
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmingDeactivate, setConfirmingDeactivate] =
    useState<DocumentTypeWithApprovers | null>(null);
  const [confirmingCustomZone, setConfirmingCustomZone] =
    useState<{ id: string; name: string } | null>(null);
  const [confirmingCustomSection, setConfirmingCustomSection] =
    useState<{ id: string; code: string } | null>(null);
  // Feedback #17/#18/#6 - scope filter tab. Undefined = everything the
  // caller can see (merged catalog for site_admin; global for platform_admin).
  const [scopeFilter, setScopeFilter] = useState<CatalogScope | undefined>(undefined);
  // Custom-taxonomy CREATE is now inline in the doc-type form's Zone /
  // Section pickers ("Other…" option). This page keeps only manage
  // actions (rename / deactivate) on custom entries.

  const taxonomyQ = useQuery({
    queryKey: ['adminEisf', 'taxonomy'],
    queryFn: () => adminEisf.getTaxonomy(),
  });

  const listQ = useQuery({
    queryKey: ['adminEisf', 'document-types', { showInactive, scopeFilter }],
    queryFn: () =>
      adminEisf.listDocumentTypes({ include_inactive: showInactive, scope: scopeFilter }),
  });

  const deactivateM = useMutation({
    mutationFn: (code: string) => adminEisf.deactivateDocumentType(code),
    onSuccess: (_, code) => {
      qc.invalidateQueries({ queryKey: ['adminEisf', 'document-types'] });
      toast.success(`Deactivated "${code}" — hidden from the binder type picker`);
      setConfirmingDeactivate(null);
    },
    onError: (err: unknown) => {
      const msg = errorText(err, 'Deactivation failed');
      toast.error(msg);
    },
  });

  const reactivateM = useMutation({
    mutationFn: (code: string) => adminEisf.updateDocumentType(code, { is_active: true }),
    onSuccess: (_, code) => {
      qc.invalidateQueries({ queryKey: ['adminEisf', 'document-types'] });
      toast.success(`Restored "${code}"`);
    },
    onError: (err: unknown) => {
      const msg = errorText(err, 'Restore failed');
      toast.error(msg);
    },
  });

  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState<string>('');
  const bulkAssignM = useMutation({
    mutationFn: () =>
      adminEisf.bulkSetParentSection({
        codes: Array.from(bulkSelected),
        parent_section_code: bulkTarget,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['adminEisf', 'document-types'] });
      setBulkSelected(new Set());
      setBulkTarget('');
      if (res.failed === 0) {
        toast.success(`Moved ${res.updated} document type${res.updated === 1 ? '' : 's'}.`);
      } else {
        toast.warning(`Moved ${res.updated}, failed ${res.failed}. Check the log for details.`);
      }
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Move failed'));
    },
  });

  // Custom-taxonomy manage mutations (deactivate only). Create happens
  // inline from the doc-type form, so no create* mutations here anymore.
  const deactivateZoneM = useMutation({
    mutationFn: (id: string) => adminCustomTaxonomy.deactivateZone(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminEisf', 'taxonomy'] });
      toast.success('Custom zone deactivated');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Deactivate zone failed'));
    },
  });
  const deactivateSectionM = useMutation({
    mutationFn: (id: string) => adminCustomTaxonomy.deactivateSection(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminEisf', 'taxonomy'] });
      toast.success('Custom section deactivated');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Deactivate section failed'));
    },
  });
  // Fetch custom entries directly (in addition to the merged taxonomy) so
  // we have their IDs for the deactivate calls - the taxonomy endpoint
  // only carries zone/section NAMES, not the custom_zone_id / custom_section_id
  // that the DELETE endpoints need on the path.
  const customZonesQ = useQuery({
    queryKey: ['adminCustomTaxonomy', 'zones'],
    queryFn: () => adminCustomTaxonomy.listZones({}),
    enabled: isPlatform,
  });
  const customSectionsQ = useQuery({
    queryKey: ['adminCustomTaxonomy', 'sections'],
    queryFn: () => adminCustomTaxonomy.listSections({}),
    enabled: isPlatform,
  });
  const customZoneByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const z of customZonesQ.data?.zones ?? []) m.set(z.name, z.custom_zone_id);
    return m;
  }, [customZonesQ.data]);
  const customSectionByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of customSectionsQ.data?.sections ?? []) m.set(s.code, s.custom_section_id);
    return m;
  }, [customSectionsQ.data]);

  /** Catalog rows keyed by lowercased document_type_name for O(1) legacy-fallback lookup. */
  const catalogByName = useMemo(() => {
    const m = new Map<string, DocumentTypeWithApprovers>();
    for (const row of listQ.data?.document_types ?? []) {
      m.set(row.document_type_name.toLowerCase().trim(), row);
    }
    return m;
  }, [listQ.data]);

  /**
   * FK-based index: `${section_code}::${lower_name}` -> row. Preferred over
   * catalogByName because it disambiguates rows whose document_type_name
   * appears in multiple taxonomy sections (e.g. "Relevant Communications"
   * is a sub-artifact in 6+ sections). The tree render checks this index
   * first, falls back to catalogByName for legacy rows with a null FK.
   */
  const catalogBySectionAndName = useMemo(() => {
    const m = new Map<string, DocumentTypeWithApprovers>();
    for (const row of listQ.data?.document_types ?? []) {
      if (row.parent_section_code) {
        const key = `${row.parent_section_code}::${row.document_type_name.toLowerCase().trim()}`;
        m.set(key, row);
      }
    }
    return m;
  }, [listQ.data]);

  /**
   * Legacy rows whose parent_section_code is null AND whose name doesn't
   * match any sub-artifact in the current taxonomy. These are invisible
   * in the normal tree; surface them in a dedicated "Unmapped" bucket at
   * the bottom so an admin can assign a Section via the edit form.
   */
  const unmappedRows = useMemo(() => {
    const zones = taxonomyQ.data?.zones ?? [];
    const knownSubartifactNames = new Set<string>();
    for (const z of zones) {
      for (const s of z.sections) {
        for (const sa of s.subartifacts) {
          knownSubartifactNames.add(sa.toLowerCase().trim());
        }
      }
    }
    return (listQ.data?.document_types ?? []).filter((row) => {
      if (row.parent_section_code) return false;
      return !knownSubartifactNames.has(row.document_type_name.toLowerCase().trim());
    });
  }, [listQ.data, taxonomyQ.data]);

  const stats = useMemo(() => {
    const data = listQ.data;
    const withRule = (data?.document_types ?? []).filter((r) => r.approvers.length > 0).length;
    return {
      total: data?.total ?? 0,
      active: data?.active ?? 0,
      inactive: data?.inactive ?? 0,
      withRule,
    };
  }, [listQ.data]);

  /**
   * Build a filtered version of the taxonomy. The query matches against:
   *   - sub-artifact names
   *   - the matching catalog row's definition / code / lifecycle
   *   - the artifact / section / zone names themselves
   * Any zone/section that ends up with no matching sub-artifacts is dropped.
   */
  const filteredZones = useMemo(() => {
    const q = search.trim().toLowerCase();
    const zones = taxonomyQ.data?.zones ?? [];
    if (!q) return zones;

    const out: TaxonomyZone[] = [];
    for (const zone of zones) {
      const zoneMatch = zone.zone.toLowerCase().includes(q);
      const matchedSections: TaxonomySection[] = [];
      for (const section of zone.sections) {
        const sectionMatch =
          section.name.toLowerCase().includes(q) ||
          section.code.toLowerCase().includes(q) ||
          section.artifactType.toLowerCase().includes(q);
        const matchedSubs = section.subartifacts.filter((sub) => {
          if (sub.toLowerCase().includes(q)) return true;
          const cat = catalogByName.get(sub.toLowerCase().trim());
          if (!cat) return false;
          return (
            cat.document_type_code.toLowerCase().includes(q) ||
            (cat.definition ?? '').toLowerCase().includes(q) ||
            (cat.lifecycle ?? '').toLowerCase().includes(q)
          );
        });
        if (zoneMatch || sectionMatch || matchedSubs.length > 0) {
          // When the section/zone itself matches, keep all subs so the
          // user sees the full context. Otherwise narrow to just the
          // matching subs.
          matchedSections.push({
            ...section,
            subartifacts: zoneMatch || sectionMatch ? section.subartifacts : matchedSubs,
          });
        }
      }
      if (matchedSections.length > 0) {
        out.push({ ...zone, sections: matchedSections });
      }
    }
    return out;
  }, [taxonomyQ.data, search, catalogByName]);

  const isLoading = taxonomyQ.isLoading || listQ.isLoading;
  const error = taxonomyQ.error ?? listQ.error;

  function openSubartifact(subName: string, sectionCode?: string) {
    // Prefer the FK-scoped row (name-in-this-section) so clicking "Relevant
    // Communications" under Regulatory takes you to the Regulatory row,
    // not whichever row wins the global name-match race.
    const fkKey = sectionCode
      ? `${sectionCode}::${subName.toLowerCase().trim()}`
      : null;
    const cat = (fkKey && catalogBySectionAndName.get(fkKey))
      || catalogByName.get(subName.toLowerCase().trim());
    if (cat) {
      // Feedback #16 - dedicated route for the doc-type editor.
      navigate(
        `/admin/eisf-master-data/document-types/${encodeURIComponent(cat.document_type_code)}/edit`,
      );
    } else {
      const params = new URLSearchParams({ prefill: subName });
      if (sectionCode) params.set('section', sectionCode);
      navigate(`/admin/eisf-master-data/document-types/new?${params.toString()}`);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="eISF Catalog"
        subtitle="The document-type master data every binder in every tenant is built from. Changes propagate immediately."
        actions={
          <>
            <BackfillMirrorsButton />
            <button
              type="button"
              onClick={() => navigate('/admin/eisf-master-data/document-types/new')}
              className="inline-flex items-center gap-2 rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
            >
              <Plus className="h-4 w-4" />
              Add document type
            </button>
          </>
        }
      />

      {isPlatform && (
        <div className="mt-4 flex items-start gap-2 rounded border border-accent-200 bg-accent-50/50 px-3 py-2 text-xs text-accent-900">
          <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            Custom zones and sections are added inline from the <b>Add document type</b> form —
            pick "Other…" in the Zone or Section dropdown to create one. To rename or deactivate
            an existing custom entry, hover its row in the tree below.
          </span>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Total types"
          value={stats.total}
          icon={<FileText className="h-4 w-4 text-ink-muted" />}
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        />
        <StatCard
          label="Hidden"
          value={stats.inactive}
          icon={<PowerOff className="h-4 w-4 text-amber-600" />}
        />
        <StatCard
          label="Configured"
          value={stats.withRule}
          icon={<ShieldCheck className="h-4 w-4 text-blue-600" />}
        />
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 rounded border border-surface-border bg-white px-3 h-10 flex-1 min-w-[260px] max-w-md">
          <Search className="h-4 w-4 text-ink-muted shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search zones, sections, document types, or definitions…"
            className="flex-1 bg-transparent outline-none text-sm text-ink-primary placeholder:text-ink-muted"
          />
        </div>
        {/* Feedback #17/#18/#6 - scope filter tabs */}
        <div className="inline-flex rounded border border-surface-border bg-white p-0.5">
          <ScopeTab
            active={scopeFilter === undefined}
            onClick={() => setScopeFilter(undefined)}
            label={isPlatform ? 'All scopes' : 'My view'}
          />
          <ScopeTab
            active={scopeFilter === 'platform'}
            onClick={() => setScopeFilter('platform')}
            label="Platform"
          />
          <ScopeTab
            active={scopeFilter === 'site'}
            onClick={() => setScopeFilter('site')}
            label={isPlatform ? 'Site' : 'My site'}
          />
          <ScopeTab
            active={scopeFilter === 'study'}
            onClick={() => setScopeFilter('study')}
            label="Study"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-ink-secondary cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4"
          />
          Show inactive
        </label>
      </div>

      {!isPlatform && (
        <div className="mt-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          You have <span className="font-semibold">site admin</span> access. Platform rows are
          read-only; use the <span className="font-semibold">My site</span> or
          <span className="font-semibold">Study</span> tabs to add or edit your own catalog entries.
        </div>
      )}

      {isLoading && (
        <div className="mt-10 flex items-center justify-center gap-2 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading taxonomy…
        </div>
      )}

      {error && (
        <div className={`mt-6 flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(error)}`}>
          {isPermissionError(error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>
            {errorText(error, 'Failed to load the eISF taxonomy.')}
          </span>
        </div>
      )}

      {!isLoading && !error && filteredZones.length === 0 && (
        <div className="mt-10 rounded border border-surface-border bg-surface-muted/30 p-10 text-center text-sm text-ink-muted">
          {search ? 'No document types match your search.' : 'The taxonomy is empty.'}
        </div>
      )}

      {filteredZones.length > 0 && (
        <div className="mt-5 rounded border border-surface-border bg-white">
          {filteredZones.map((zone) => (
            <ZoneNode
              key={zone.zone}
              zone={zone}
              catalogByName={catalogByName}
              catalogBySectionAndName={catalogBySectionAndName}
              catalogRows={listQ.data?.document_types ?? []}
              autoExpand={!!search.trim()}
              onOpen={openSubartifact}
              onDeactivate={(row) => setConfirmingDeactivate(row)}
              onReactivate={(code) => reactivateM.mutate(code)}
              reactivatePending={reactivateM.isPending}
              onDeactivateCustomZone={
                zone.isCustom && customZoneByName.has(zone.zone)
                  ? () =>
                      setConfirmingCustomZone({
                        id: customZoneByName.get(zone.zone)!,
                        name: zone.zone,
                      })
                  : undefined
              }
              onDeactivateCustomSection={(sectionCode) => {
                const id = customSectionByCode.get(sectionCode);
                if (id) setConfirmingCustomSection({ id, code: sectionCode });
              }}
              isPlatform={isPlatform}
            />
          ))}
        </div>
      )}

      {/* Unmapped bucket - catalog rows with no parent_section_code AND
          no legacy sub-artifact name match. Surface them so an admin can
          click into the edit form and pick a Section. */}
      {!isLoading && !error && unmappedRows.length > 0 && (
        <div className="mt-4 rounded border border-amber-200 bg-amber-50/60">
          <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-900">
              Needs a section ({unmappedRows.length})
            </span>
            <span className="text-xs text-amber-700">
              — tick rows to move several at once, or click a row to edit it.
            </span>
            {bulkSelected.size > 0 && (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-amber-900">
                  {bulkSelected.size} selected
                </span>
                <select
                  value={bulkTarget}
                  onChange={(e) => setBulkTarget(e.target.value)}
                  disabled={bulkAssignM.isPending}
                  className="rounded border border-amber-300 bg-white px-2 py-1 text-xs text-ink-primary"
                >
                  <option value="">Move to section…</option>
                  {(taxonomyQ.data?.zones ?? []).flatMap((z) =>
                    z.sections.map((s) => (
                      <option key={s.code} value={s.code}>
                        {z.zone} › {s.name}
                      </option>
                    )),
                  )}
                </select>
                <button
                  type="button"
                  onClick={() => bulkAssignM.mutate()}
                  disabled={!bulkTarget || bulkAssignM.isPending}
                  className="inline-flex items-center gap-1.5 rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-60"
                >
                  {bulkAssignM.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : null}
                  Move
                </button>
                <button
                  type="button"
                  onClick={() => setBulkSelected(new Set())}
                  disabled={bulkAssignM.isPending}
                  className="text-xs text-amber-800 hover:text-amber-900"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <ul className="divide-y divide-amber-100">
            {unmappedRows.map((row) => {
              const checked = bulkSelected.has(row.document_type_code);
              return (
                <li key={row.document_type_def_id} className="flex items-center gap-2 px-4 py-2 hover:bg-amber-100/60">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = new Set(bulkSelected);
                      if (next.has(row.document_type_code)) next.delete(row.document_type_code);
                      else next.add(row.document_type_code);
                      setBulkSelected(next);
                    }}
                    className="h-3.5 w-3.5 shrink-0"
                    aria-label={`Select ${row.document_type_name}`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/admin/eisf-master-data/document-types/${encodeURIComponent(row.document_type_code)}/edit`,
                      )
                    }
                    className="flex flex-1 items-center gap-2 text-left text-sm"
                  >
                    <FileText className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                    <span className="text-ink-primary">{row.document_type_name}</span>
                    {!row.is_active && (
                      <span className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                        Hidden
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Edit / Create now live on their own routes (Feedback #16). The
          list navigates to /admin/eisf-master-data/document-types/:code/edit
          or /admin/eisf-master-data/document-types/new?prefill=... */}

      {confirmingDeactivate && (
        <DeactivateConfirmModal
          row={confirmingDeactivate}
          isPending={deactivateM.isPending}
          onCancel={() => setConfirmingDeactivate(null)}
          onConfirm={() => deactivateM.mutate(confirmingDeactivate.document_type_code)}
        />
      )}

      {confirmingCustomZone && (
        <ConfirmModal
          title="Hide custom zone"
          body={
            <p>
              Hide the custom zone <b>{confirmingCustomZone.name}</b>? It stays in the database
              but is no longer visible in the Catalog or the Add-document-type picker.
            </p>
          }
          confirmLabel={deactivateZoneM.isPending ? 'Hiding…' : 'Hide'}
          isPending={deactivateZoneM.isPending}
          onCancel={() => setConfirmingCustomZone(null)}
          onConfirm={() => {
            deactivateZoneM.mutate(confirmingCustomZone.id, {
              onSuccess: () => setConfirmingCustomZone(null),
            });
          }}
        />
      )}

      {confirmingCustomSection && (
        <ConfirmModal
          title="Hide custom section"
          body={
            <p>
              Hide the custom section <b>{confirmingCustomSection.code}</b>? Documents
              already filed here stay put; new uploads will need a different section.
            </p>
          }
          confirmLabel={deactivateSectionM.isPending ? 'Hiding…' : 'Hide'}
          isPending={deactivateSectionM.isPending}
          onCancel={() => setConfirmingCustomSection(null)}
          onConfirm={() => {
            deactivateSectionM.mutate(confirmingCustomSection.id, {
              onSuccess: () => setConfirmingCustomSection(null),
            });
          }}
        />
      )}

    </div>
  );
}

function ZoneNode({
  zone,
  catalogByName,
  catalogBySectionAndName,
  catalogRows,
  autoExpand,
  onOpen,
  onDeactivate,
  onReactivate,
  reactivatePending,
  onDeactivateCustomZone,
  onDeactivateCustomSection,
  isPlatform,
}: {
  zone: TaxonomyZone;
  catalogByName: Map<string, DocumentTypeWithApprovers>;
  catalogBySectionAndName: Map<string, DocumentTypeWithApprovers>;
  catalogRows: DocumentTypeWithApprovers[];
  autoExpand: boolean;
  onOpen: (subName: string, sectionCode?: string) => void;
  onDeactivate: (row: DocumentTypeWithApprovers) => void;
  onReactivate: (code: string) => void;
  reactivatePending: boolean;
  /** undefined when the zone is DIA reference (no deactivate action). */
  onDeactivateCustomZone?: () => void;
  onDeactivateCustomSection: (sectionCode: string) => void;
  isPlatform: boolean;
}) {
  const [open, setOpen] = useState(autoExpand);
  useMemoEffect(() => setOpen(autoExpand), [autoExpand]);

  const totalSubs = zone.sections.reduce((sum, s) => sum + s.subartifacts.length, 0);

  const configured = zone.sections.reduce((sum, s) => {
    return sum + s.subartifacts.filter((sub) => {
      const bySection = catalogBySectionAndName.get(`${s.code}::${sub.toLowerCase()}`);
      const byName = catalogByName.get(sub.toLowerCase());
      return !!(bySection ?? byName);
    }).length;
  }, 0);
  const coverageIncomplete = totalSubs > 0 && configured < totalSubs;

  return (
    <div className="border-b border-surface-border/40 last:border-0">
      <div className="group flex w-full items-center gap-2 px-4 py-3 hover:bg-surface-muted/30">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 text-ink-muted shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" />
          )}
          <Folder className={`h-4 w-4 shrink-0 ${zone.isCustom ? 'text-violet-600' : 'text-accent-600'}`} />
          <span className="font-semibold text-ink-primary">{zone.zone}</span>
          {zone.isCustom && (
            <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
              <Sparkles className="h-3 w-3" /> custom
            </span>
          )}
          <span className="text-xs text-ink-muted">
            {zone.sections.length} section{zone.sections.length === 1 ? '' : 's'}
            {totalSubs > 0
              ? ` · ${totalSubs} document type${totalSubs === 1 ? '' : 's'}`
              : ''}
          </span>
          {totalSubs > 0 && (
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                coverageIncomplete
                  ? 'bg-amber-50 text-amber-800'
                  : 'bg-emerald-50 text-emerald-700'
              }`}
              title={
                coverageIncomplete
                  ? `${totalSubs - configured} document type${totalSubs - configured === 1 ? '' : 's'} still need setup.`
                  : 'Every document type in this zone is configured.'
              }
            >
              {configured} of {totalSubs} configured
            </span>
          )}
        </button>
        {isPlatform && onDeactivateCustomZone && (
          <div className="flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDeactivateCustomZone();
              }}
              className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
              title="Deactivate this custom zone (refuses if custom sections still live here)"
            >
              <PowerOff className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
      {open && (
        <div className="pb-2">
          {zone.sections.map((section) => (
            <SectionNode
              key={section.code}
              section={section}
              catalogByName={catalogByName}
              catalogBySectionAndName={catalogBySectionAndName}
              catalogRows={catalogRows}
              autoExpand={autoExpand}
              onOpen={onOpen}
              onDeactivate={onDeactivate}
              onReactivate={onReactivate}
              reactivatePending={reactivatePending}
              onDeactivateCustomSection={onDeactivateCustomSection}
              isPlatform={isPlatform}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionNode({
  section,
  catalogByName,
  catalogBySectionAndName,
  catalogRows,
  autoExpand,
  onOpen,
  onDeactivate,
  onReactivate,
  reactivatePending,
  onDeactivateCustomSection,
  isPlatform,
}: {
  section: TaxonomySection;
  catalogByName: Map<string, DocumentTypeWithApprovers>;
  catalogBySectionAndName: Map<string, DocumentTypeWithApprovers>;
  catalogRows: DocumentTypeWithApprovers[];
  autoExpand: boolean;
  onOpen: (subName: string, sectionCode?: string) => void;
  onDeactivate: (row: DocumentTypeWithApprovers) => void;
  onReactivate: (code: string) => void;
  reactivatePending: boolean;
  onDeactivateCustomSection: (sectionCode: string) => void;
  isPlatform: boolean;
}) {
  const [open, setOpen] = useState(autoExpand);
  useMemoEffect(() => setOpen(autoExpand), [autoExpand]);

  const customDocTypeRows = useMemo(
    () =>
      section.isCustom
        ? catalogRows.filter((r) => r.parent_section_code === section.code)
        : [],
    [section.isCustom, section.code, catalogRows],
  );

  const sectionCoverage = useMemo(() => {
    if (section.isCustom) {
      return { configured: customDocTypeRows.length, total: customDocTypeRows.length };
    }
    let configured = 0;
    for (const sub of section.subartifacts) {
      const bySection = catalogBySectionAndName.get(`${section.code}::${sub.toLowerCase()}`);
      const byName = catalogByName.get(sub.toLowerCase());
      if (bySection ?? byName) configured++;
    }
    return { configured, total: section.subartifacts.length };
  }, [section, customDocTypeRows.length, catalogBySectionAndName, catalogByName]);
  const sectionIncomplete = sectionCoverage.total > 0 && sectionCoverage.configured < sectionCoverage.total;

  /**
   * Group sub-artifacts by their `artifactType`. When every sub-artifact
   * in a section shares the same artifact type (the common case), we
   * skip the level-3 "Artifact" header so the tree doesn't feel padded
   * with a one-child group. When the section spans multiple artifact
   * types, each type gets its own row.
   *
   * Level 3 in the spec is "Artifact (where unique)" — we read "unique"
   * as "distinct grouping needed", i.e. show the artifact header only
   * when it differs across sub-artifacts within the same section.
   */
  const showArtifactRow = false; // Section-level artifact_type is uniform per the BE schema.

  return (
    <div className="ml-4 border-l border-surface-border/40">
      <div className="group flex w-full items-center gap-2 px-3 py-2 hover:bg-surface-muted/30">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-ink-muted shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-ink-muted shrink-0" />
          )}
          <Folder className={`h-3.5 w-3.5 shrink-0 ${section.isCustom ? 'text-violet-500' : 'text-ink-muted'}`} />
          <span className="text-sm font-medium text-ink-primary">{section.name}</span>
          <span className="text-[11px] text-ink-muted font-mono">{section.code}</span>
          {section.isCustom && (
            <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
              <Sparkles className="h-3 w-3" /> custom
            </span>
          )}
          <span
            className={`ml-1 inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${
              section.inclusion === 'core'
                ? 'bg-blue-50 text-blue-700'
                : 'bg-slate-100 text-slate-700'
            }`}
          >
            {section.inclusion === 'core' ? 'Required' : 'Recommended'}
          </span>
          {sectionCoverage.total > 0 && (
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                sectionIncomplete
                  ? 'bg-amber-50 text-amber-800'
                  : 'bg-emerald-50 text-emerald-700'
              }`}
              title={
                sectionIncomplete
                  ? `${sectionCoverage.total - sectionCoverage.configured} document type${sectionCoverage.total - sectionCoverage.configured === 1 ? '' : 's'} still need setup.`
                  : 'Every document type in this section is configured.'
              }
            >
              {sectionCoverage.configured} of {sectionCoverage.total} configured
            </span>
          )}
        </button>
        {isPlatform && section.isCustom && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDeactivateCustomSection(section.code);
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 opacity-0 group-hover:opacity-100 hover:bg-amber-100 transition-opacity"
            title="Deactivate this custom section (refuses if active doc types still filed here)"
          >
            <PowerOff className="h-3 w-3" />
          </button>
        )}
      </div>
      {open && (
        <div className="pb-1.5">
          {showArtifactRow && (
            <div className="ml-6 flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-wider text-ink-muted">
              <Folder className="h-3 w-3" />
              {section.artifactType}
            </div>
          )}
          {/* Custom sections: iterate doc types filed here via parent_section_code
              (the taxonomy carries no pre-declared subartifacts for custom sections). */}
          {section.isCustom && (
            <>
              {customDocTypeRows.length === 0 && (
                <div className="ml-6 px-3 py-2 text-xs italic text-ink-muted">
                  No document types filed in this custom section yet. Click "Add document type"
                  and pick this section from the Zone / Section picker.
                </div>
              )}
              {customDocTypeRows.map((row) => (
                <SubartifactNode
                  key={row.document_type_def_id}
                  name={row.document_type_name}
                  sectionCode={section.code}
                  row={row}
                  onOpen={onOpen}
                  onDeactivate={onDeactivate}
                  onReactivate={onReactivate}
                  reactivatePending={reactivatePending}
                />
              ))}
            </>
          )}
          {!section.isCustom && section.subartifacts.length === 0 && (
            <div className="ml-6 px-3 py-2 text-xs italic text-ink-muted">
              No sub-artifacts in this section.
            </div>
          )}
          {section.subartifacts.map((sub) => {
            // Prefer FK-scoped row (name + section_code) so a name that
            // repeats across sections resolves to the right row here.
            // Fall back to legacy name-only match for null-FK rows.
            const fkKey = `${section.code}::${sub.toLowerCase().trim()}`;
            const row = catalogBySectionAndName.get(fkKey)
              ?? catalogByName.get(sub.toLowerCase().trim());
            return (
              <SubartifactNode
                key={sub}
                name={sub}
                sectionCode={section.code}
                row={row}
                onOpen={onOpen}
                onDeactivate={onDeactivate}
                onReactivate={onReactivate}
                reactivatePending={reactivatePending}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function SubartifactNode({
  name,
  sectionCode,
  row,
  onOpen,
  onDeactivate,
  onReactivate,
  reactivatePending,
}: {
  name: string;
  sectionCode: string;
  row: DocumentTypeWithApprovers | undefined;
  onOpen: (name: string, sectionCode?: string) => void;
  onDeactivate: (row: DocumentTypeWithApprovers) => void;
  onReactivate: (code: string) => void;
  reactivatePending: boolean;
}) {
  const hasRules = !!row;
  return (
    <div
      className={`group ml-6 flex items-center gap-2 rounded px-3 py-2 text-sm ${
        hasRules
          ? 'hover:bg-surface-muted/40 cursor-pointer'
          : 'hover:bg-accent-50/40 cursor-pointer'
      }`}
      onClick={() => onOpen(name, sectionCode)}
    >
      <FileText
        className={`h-3.5 w-3.5 shrink-0 ${hasRules ? 'text-ink-muted' : 'text-ink-muted/60'}`}
      />
      <span className={hasRules ? 'text-ink-primary' : 'text-ink-muted'}>{name}</span>

      {hasRules ? (
        <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
          <CheckCircle2 className="h-3 w-3" />
          Configured
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
          Not set up
        </span>
      )}
      {hasRules && row && row.is_mandatory === 'true' && (
        <span
          className="inline-flex items-center gap-1 rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-800"
          title="Required in every binder template."
        >
          Required
        </span>
      )}

      {/* Feedback #17/#18/#6 - scope badge. Platform is default (no
          badge, most rows). Site / Study override are called out so
          site_admins can spot their own additions quickly. */}
      {hasRules && row && row.scope === 'site' && (
        <span
          className="inline-flex rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
          title="Site-scoped: visible only in this site's binders"
        >
          site
        </span>
      )}
      {hasRules && row && row.scope === 'study' && (
        <span
          className="inline-flex rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
          title="Study-scoped: visible only in the owner study's binder"
        >
          study
        </span>
      )}

      {hasRules && row && !row.is_active && (
        <span className="inline-flex rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
          Hidden
        </span>
      )}

      <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {hasRules && row ? (
          <>
            {row.is_active ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeactivate(row);
                }}
                className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
              >
                <PowerOff className="h-3 w-3" /> Deactivate
              </button>
            ) : (
              <button
                type="button"
                disabled={reactivatePending}
                onClick={(e) => {
                  e.stopPropagation();
                  onReactivate(row.document_type_code);
                }}
                className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
              >
                <Power className="h-3 w-3" /> Reactivate
              </button>
            )}
          </>
        ) : (
          <span className="inline-flex items-center gap-1 rounded border border-dashed border-accent-300 px-2 py-0.5 text-[11px] font-medium text-accent-700">
            <Plus className="h-3 w-3" /> Define rules
          </span>
        )}
      </span>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="rounded border border-surface-border bg-white p-3">
      <div className="flex items-center justify-between text-xs text-ink-muted">
        <span>{label}</span>
        {icon}
      </div>
      <div className="mt-1 text-2xl font-semibold text-ink-primary">{value}</div>
    </div>
  );
}

function DeactivateConfirmModal({
  row,
  isPending,
  onCancel,
  onConfirm,
}: {
  row: DocumentTypeWithApprovers;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden
      />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="space-y-2 min-w-0">
            <h3 className="text-sm font-semibold text-ink-primary">
              Deactivate this document type?
            </h3>
            <p className="text-xs text-ink-muted">
              <span className="font-medium text-ink-primary">{row.document_type_name}</span> will be
              hidden from the binder type picker. Existing slots that already reference this type
              keep working — only new uploads can't pick it. Reactivation is one click away.
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center gap-1.5 rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:opacity-60"
          >
            {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            Deactivate
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tiny wrapper around `useEffect` so the call sites read as "react to
 * deps changing" rather than "kick off a side effect". Used for the
 * search-driven auto-expand of tree nodes.
 */
function useMemoEffect(fn: () => void, deps: ReadonlyArray<unknown>) {
  useEffect(fn, deps);
}

// Custom-zone / custom-section create modals were removed - creation is
// now inline via the DocumentTypeFormPage's Zone / Section "Other…"
// picker. This page keeps only manage actions (deactivate) on custom
// entries via the hover-reveal button on each tree row.

/** Feedback #17/#18/#6 - scope filter tab. */
function ScopeTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center rounded px-2.5 py-1 text-xs font-medium ${
        active ? 'bg-ink-primary text-white' : 'text-ink-secondary hover:bg-surface-muted/60'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * One-shot operational button that mirrors every historical
 * consent-document / signed-consent / CSR / DoA log record into its
 * eISF binder slot. Restricted to platform_admin (the endpoint enforces
 * this too; the FE gate is UX-only).
 *
 * Renders as a small button next to "Add document type". After a run,
 * a toast reports totals plus a per-flow breakdown so the operator can
 * cross-check counts. Idempotent - safe to re-run at any time.
 */
function BackfillMirrorsButton() {
  const qc = useQueryClient();
  const canRun = useHasRole(['platform_admin', 'application_admin']);

  const runM = useMutation({
    mutationFn: () => adminEisf.backfillMirrors(),
    onSuccess: (summary) => {
      const total =
        summary.blank_icf.mirrored +
        summary.signed_icf.mirrored +
        summary.clinical_study_report.mirrored +
        summary.doa_log.mirrored;
      const failed =
        summary.blank_icf.failed +
        summary.signed_icf.failed +
        summary.clinical_study_report.failed +
        summary.doa_log.failed;
      const parts = [
        `Blank ICF: ${summary.blank_icf.mirrored}/${summary.blank_icf.seen}`,
        `Signed ICF: ${summary.signed_icf.mirrored}/${summary.signed_icf.seen}`,
        `CSR: ${summary.clinical_study_report.mirrored}/${summary.clinical_study_report.seen}`,
        `DoA log: ${summary.doa_log.mirrored}/${summary.doa_log.seen}`,
      ];
      const msg = `Backfill complete - ${total} mirrored${failed ? `, ${failed} failed` : ''}. ${parts.join(' · ')}`;
      if (failed > 0) toast.error(msg);
      else toast.success(msg);
      qc.invalidateQueries({ queryKey: ['binder'] });
      qc.invalidateQueries({ queryKey: ['eisf'] });
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Backfill failed'));
    },
  });

  if (!canRun) return null;

  return (
    <button
      type="button"
      onClick={() => runM.mutate()}
      disabled={runM.isPending}
      className="inline-flex items-center gap-2 rounded border border-surface-border bg-white px-3 py-2 text-sm font-medium text-ink-primary hover:bg-surface-muted disabled:opacity-50"
      title="Mirror any historical consent / signed / CSR / DoA records that are missing an eISF binder slot. Idempotent."
    >
      {runM.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4" />
      )}
      {runM.isPending ? 'Backfilling…' : 'Backfill eISF mirrors'}
    </button>
  );
}
